/**
 * PanelKatalog — ekran "Katalog APISAI" (UI FREEZE v1.0, jasny styl 1:1 z makietą).
 *
 * Dane: realne (KPI + delty + źródła ruchu z /api/metrics/dashboard, posty z /api/metrics/promo)
 *       + uczciwe empty-state gdy brak danych (zero zmyślonych statusów).
 * Layout: header → baner kompletności → 4 karty KPI → 3 kolumny → szybkie działania → obecność online.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Eye, MessageSquare, Phone, Send, Users,
  Camera, FileText, Globe, Facebook, Search, MapPin, Dog,
  CheckCircle2, ArrowUpRight, ArrowRight, ExternalLink, Loader2, Briefcase,
} from 'lucide-react';
import StudioShell from '@/components/studio/StudioShell';
import { API } from '@/utils/api';

const PRIMARY = '#F97316';

/* Mapowanie źródeł ruchu — współdzielone przez widok hodowli i firmy */
const SOURCE_META = {
  catalog: { label: 'Katalog APISAI', color: PRIMARY },
  google: { label: 'Google', color: '#10B981' },
  facebook: { label: 'Facebook', color: '#1877F2' },
  instagram: { label: 'Instagram', color: '#E1306C' },
  direct: { label: 'Bezpośrednie', color: '#94A3B8' },
  referral: { label: 'Inne strony', color: '#7C5CE6' },
  ai_search: { label: 'AI Search', color: '#F59E0B' },
  bing: { label: 'Bing', color: '#0089D7' },
};

/* Donut chart (custom SVG, jasny track) */
function DonutChart({ data, size = 150 }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const radius = size / 2 - 14;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#F1EFEC" strokeWidth="16" />
      {data.map((d, i) => {
        const pct = d.value / total;
        const len = pct * circumference;
        const seg = (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={d.color}
            strokeWidth="16"
            strokeDasharray={`${len} ${circumference - len}`}
            strokeDashoffset={-offset}
            strokeLinecap="butt"
            className="transition-all duration-500"
          />
        );
        offset += len;
        return seg;
      })}
    </svg>
  );
}

/* Karta KPI (1:1 z ekranem Zapytania) */
function StatCard({ Icon, iconColor, iconBg, value, label, sub, subColor, testid }) {
  return (
    <div className="apisai-soft-card p-5 flex gap-4" data-testid={testid}>
      <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: iconBg }}>
        <Icon className="w-5 h-5" style={{ color: iconColor }} strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <p className="text-[var(--apisai-text-primary)] text-[28px] font-bold leading-none tracking-tight tabular-nums">{value}</p>
        <p className="text-[var(--apisai-text-secondary)] text-[13px] font-medium mt-1.5 leading-snug">{label}</p>
        {sub && <p className="text-[11px] font-semibold mt-1.5" style={{ color: subColor }}>{sub}</p>}
      </div>
    </div>
  );
}

/* Karta szybkiego działania */
function QuickActionCard({ Icon, iconColor, iconBg, title, desc, cta, onClick, testid }) {
  return (
    <div className="apisai-soft-card p-5 flex flex-col" data-testid={testid}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: iconBg }}>
        <Icon className="w-5 h-5" style={{ color: iconColor }} strokeWidth={2} />
      </div>
      <p className="text-[15px] font-bold text-[var(--apisai-text-primary)]">{title}</p>
      <p className="text-[13px] text-[var(--apisai-text-secondary)] mt-1 leading-snug flex-1">{desc}</p>
      <button
        onClick={onClick}
        className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--apisai-primary)] hover:underline self-start"
      >
        {cta} <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* Wiersz obecności online */
function PresenceRow({ Icon, iconColor, name, status, done }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${iconColor}18`, color: iconColor }}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[var(--apisai-text-primary)] text-sm font-semibold">{name}</p>
        <p className={`text-[12px] font-medium ${done ? 'text-emerald-600' : 'text-[var(--apisai-text-muted)]'}`}>{status}</p>
      </div>
      {done
        ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        : <ArrowUpRight className="w-4 h-4 text-zinc-300 flex-shrink-0" />}
    </div>
  );
}

/* Kompletność profilu — uczciwa miara wypełnienia pól hodowli */
function computeCompleteness(kennel, dogsCount) {
  if (!kennel) return 0;
  const checks = [
    !!kennel.name,
    (kennel.description || kennel.about || '').length > 40,
    !!(kennel.cover_image || kennel.logo || kennel.logo_url),
    !!((kennel.gallery && kennel.gallery.length) || (kennel.photos && kennel.photos.length)),
    !!(kennel.city || kennel.location || kennel.address),
    !!(kennel.phone || kennel.whatsapp || kennel.phone_number),
    !!kennel.email,
    dogsCount > 0,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

/* Kompletność profilu firmy — uczciwa miara wypełnienia pól firmy */
function computeBusinessCompleteness(b) {
  if (!b) return 0;
  const checks = [
    !!b.name,
    (b.description || b.short_description || '').length > 40,
    !!(b.logo || b.cover_photo),
    !!((b.photos && b.photos.length) || (b.portfolio && b.portfolio.length)),
    !!(b.services && b.services.length),
    !!(b.city || b.address),
    !!(b.phone || b.whatsapp),
    !!b.email,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

const trendSub = (delta) =>
  (delta === undefined || delta === null)
    ? 'w ostatnich 30 dniach'
    : `${delta >= 0 ? '↑' : '↓'} ${Math.abs(delta)}% w ostatnich 30 dniach`;
const trendColor = (delta) =>
  (delta === undefined || delta === null) ? '#71717A' : (delta >= 0 ? '#10B981' : '#DC2626');

export default function PanelKatalog() {
  const navigate = useNavigate();

  // Biznes ma własny, lekki widok katalogu W PANELU — nie wyrzucamy go na publiczny katalog.
  const isBiznes = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.profile_type === 'biznes'; }
    catch { return false; }
  }, []);

  const [loading, setLoading] = useState(true);
  const [kennel, setKennel] = useState(null);
  const [business, setBusiness] = useState(null);
  const [completeness, setCompleteness] = useState(0);
  const [position, setPosition] = useState(null);
  const [data, setData] = useState({
    visits: 0, messages: 0, contact_clicks: 0, active_posts: 0,
    visits_delta: undefined, messages_delta: undefined, contact_delta: undefined, posts_delta: undefined,
    top_pets: [], posts: [], traffic_sources: [],
  });

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    if (isBiznes) return; // biznes ma osobny fetch poniżej
    (async () => {
      try {
        const kRes = await axios.get(`${API}/kennels`, { headers: headers() }).catch(() => ({ data: [] }));
        const kennels = kRes.data || [];
        const myK = Array.isArray(kennels) && kennels[0];
        setKennel(myK);
        const kid = myK?.id;
        const [dRes, sRes, litRes, mRes, pRes, posRes] = await Promise.allSettled([
          kid ? axios.get(`${API}/kennels/${kid}/dogs`, { headers: headers() }) : Promise.resolve({ data: [] }),
          axios.get(`${API}/studs`, { headers: headers() }).catch(() => ({ data: [] })),
          axios.get(`${API}/litters`, { headers: headers() }).catch(() => ({ data: [] })),
          axios.get(`${API}/metrics/dashboard?days=30`, { headers: headers() }).catch(() => null),
          axios.get(`${API}/metrics/promo?days=90`, { headers: headers() }).catch(() => null),
          axios.get(`${API}/metrics/catalog-position`, { headers: headers() }).catch(() => null),
        ]);
        const dogs = dRes.status === 'fulfilled' ? (dRes.value.data || []) : [];
        const studs = sRes.status === 'fulfilled' ? (sRes.value.data || []) : [];
        const litters = litRes.status === 'fulfilled' ? (litRes.value.data || []) : [];
        const metrics = mRes.status === 'fulfilled' && mRes.value ? mRes.value.data : null;
        const promo = pRes.status === 'fulfilled' && pRes.value ? pRes.value.data : null;
        setPosition(posRes.status === 'fulfilled' && posRes.value ? posRes.value.data : null);

        setCompleteness(computeCompleteness(myK, dogs.length + studs.length));

        // Top pets z realnych bytów (reproduktory → psy → mioty)
        const top_pets = [
          ...studs.map((d) => ({
            id: d.id, name: d.name || 'Reproduktor', subtitle: 'Reproduktor',
            img: d.main_photo || d.cover_image || (d.photos && d.photos[0]) || (d.gallery && d.gallery[0]),
            views: 0, color: '#3B82F6',
          })),
          ...dogs.map((d) => ({
            id: d.id, name: d.name || 'Pies',
            subtitle: d.gender === 'female' || d.listing_type === 'suka' ? 'Suka hodowlana' : 'Reproduktor',
            img: d.main_photo || d.cover_image || (d.gallery && d.gallery[0]),
            views: 0, color: (d.gender === 'female' || d.listing_type === 'suka') ? '#EC4899' : '#3B82F6',
          })),
          ...litters.map((l) => ({
            id: l.id, name: l.name || `Miot ${l.breed || ''}`.trim(),
            subtitle: l.status === 'available' ? 'Dostępne szczenięta' : 'Miot',
            img: l.cover_image || (l.gallery && l.gallery[0]),
            views: 0, color: '#F59E0B',
          })),
        ].filter((p) => p.id).slice(0, 3);

        if (metrics?.top_offers?.length) {
          const viewMap = new Map(metrics.top_offers.map((o) => [o.entity_id, o.views]));
          top_pets.forEach((p) => { p.views = viewMap.get(p.id) || 0; });
        }

        // Źródła ruchu (realne)
        const traffic_sources = (metrics?.traffic_sources || []).map((s) => ({
          label: SOURCE_META[s.source]?.label || s.source,
          value: s.count,
          color: SOURCE_META[s.source]?.color || '#F97316',
          pct: s.pct,
        }));

        // Ostatnie posty (realne, z /metrics/promo)
        const posts = (promo?.posts || []).slice(0, 3).map((p) => ({
          id: p.id, title: p.title || 'Bez tytułu', age: p.age || 'niedawno',
          views: p.reach || p.visits || 0,
        }));

        const kpis = metrics?.kpis || {};
        const realDelta = (k) => (kpis[k]?.prev > 0 ? kpis[k]?.delta : undefined);

        setData({
          visits: kpis.visits?.value ?? 0,
          visits_delta: realDelta('visits'),
          messages: kpis.messages?.value ?? 0,
          messages_delta: realDelta('messages'),
          contact_clicks: kpis.contact_clicks?.value ?? 0,
          contact_delta: realDelta('contact_clicks'),
          active_posts: kpis.active_posts?.value ?? 0,
          posts_delta: realDelta('active_posts'),
          top_pets,
          posts,
          traffic_sources,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Biznes: pobierz dane firmy + realne metryki (parytet z widokiem hodowli).
  useEffect(() => {
    if (!isBiznes) return;
    (async () => {
      try {
        const [bRes, mRes] = await Promise.allSettled([
          axios.get(`${API}/business/my`, { headers: headers() }),
          axios.get(`${API}/metrics/dashboard?days=30`, { headers: headers() }),
        ]);
        const bList = bRes.status === 'fulfilled'
          ? (Array.isArray(bRes.value.data) ? bRes.value.data : (bRes.value.data ? [bRes.value.data] : []))
          : [];
        const myB = bList[0] || null;
        setBusiness(myB);
        setCompleteness(computeBusinessCompleteness(myB));

        const metrics = mRes.status === 'fulfilled' && mRes.value ? mRes.value.data : null;

        const traffic_sources = (metrics?.traffic_sources || []).map((s) => ({
          label: SOURCE_META[s.source]?.label || s.source,
          value: s.count,
          color: SOURCE_META[s.source]?.color || '#F97316',
          pct: s.pct,
        }));

        const OFFER_LABEL = { business: 'Strona firmy', service: 'Usługa', service_view: 'Usługa' };
        const top_pets = (metrics?.top_offers || []).slice(0, 3).map((o) => ({
          id: o.entity_id || o.entity_slug || o.name,
          name: o.entity_type === 'business' ? (myB?.name || 'Strona firmy') : (o.name || 'Oferta'),
          subtitle: OFFER_LABEL[o.entity_type] || 'Oferta',
          img: null,
          views: o.views || 0,
          color: '#F97316',
        })).filter((p) => p.id);

        const kpis = metrics?.kpis || {};
        const realDelta = (k) => (kpis[k]?.prev > 0 ? kpis[k]?.delta : undefined);
        setData({
          visits: kpis.visits?.value ?? 0,
          visits_delta: realDelta('visits'),
          messages: kpis.messages?.value ?? 0,
          messages_delta: realDelta('messages'),
          contact_clicks: kpis.contact_clicks?.value ?? 0,
          contact_delta: realDelta('contact_clicks'),
          active_posts: kpis.active_posts?.value ?? 0,
          posts_delta: realDelta('active_posts'),
          top_pets,
          posts: [],
          traffic_sources,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [isBiznes]);

  const trafficSources = useMemo(() => {
    if (data.traffic_sources?.length) return data.traffic_sources;
    return [{ label: 'Czekamy na pierwsze wizyty…', value: 1, color: '#E8E5E0', pct: 100 }];
  }, [data.traffic_sources]);

  if (loading) {
    return (
      <StudioShell mode="workspace" activeKey="catalog" contextLabel="Katalog APISAI">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[var(--apisai-primary)] animate-spin" />
        </div>
      </StudioShell>
    );
  }

  // ── Widok katalogu dla BIZNESU (parytet z widokiem hodowli) ──
  if (isBiznes) {
    const published = !!(business && (business.is_published || business.show_in_catalog));
    const bizId = business?.id;
    const editPath = bizId ? `/wizard/firma-v2/${bizId}` : '/wizard/firma-v2';
    const hasSocial = !!(business?.social_facebook || business?.social_instagram);

    // Pełny dashboard firmy (1:1 ze strukturą widoku hodowli) — renderowany ZAWSZE,
    // także gdy firma nie istnieje (parytet: hodowca też pokazuje bogaty layout przy 0%).
    return (
      <StudioShell mode="workspace" activeKey="catalog" contextLabel="Katalog APISAI">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 space-y-6" data-testid="panel-katalog-biznes">
          {/* Header */}
          <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 flex-wrap">
            <div className="space-y-1">
              <h1 className="text-[var(--apisai-text-primary)] text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Katalog APISAI</h1>
              <p className="text-[var(--apisai-text-secondary)] text-sm sm:text-base">Zarządzaj widocznością firmy i sprawdzaj efekty.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {published && business.slug && (
                <a href={`/firma/${business.slug}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--apisai-primary)] hover:underline"
                  data-testid="biznes-view-public">
                  Zobacz mój profil w katalogu <ExternalLink className="w-4 h-4" />
                </a>
              )}
              <button onClick={() => navigate(editPath)}
                className="inline-flex items-center gap-2 px-5 h-11 rounded-xl bg-[var(--apisai-primary)] text-white text-[14px] font-semibold hover:brightness-95 transition-all shadow-sm"
                data-testid="biznes-edit-page">
                {!business ? 'Utwórz stronę firmy' : (published ? 'Edytuj stronę firmy' : 'Opublikuj stronę firmy')}
              </button>
            </div>
          </header>

          {/* Baner kompletności profilu */}
          <section
            className="rounded-2xl border px-5 sm:px-7 py-5 flex flex-col lg:flex-row lg:items-center gap-5"
            style={{ background: '#FFF8F1', borderColor: 'rgba(249,115,22,0.15)' }}
            data-testid="biznes-completeness-banner"
          >
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(249,115,22,0.12)' }}>
                <Sparkles className="w-6 h-6 text-[var(--apisai-primary)]" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[var(--apisai-text-primary)] text-[15px] sm:text-base font-bold">
                    {published ? 'Twoja firma jest widoczna w katalogu APISAI' : 'Dokończ profil firmy, aby pojawić się w katalogu'}
                  </p>
                  {published && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
                </div>
                <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-1">
                  Zadbaj o kompletność profilu firmy, aby zdobywać więcej klientów.
                </p>
              </div>
            </div>
            <div className="lg:w-72 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-[var(--apisai-text-secondary)]">Kompletność profilu</span>
                <span className="text-[15px] font-bold text-[var(--apisai-primary)]" data-testid="biznes-completeness-value">{completeness}%</span>
              </div>
              <div className="h-2 rounded-full bg-white overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${completeness}%`, background: 'linear-gradient(90deg,#34D399,#10B981)' }} />
              </div>
              <button onClick={() => navigate(editPath)} className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--apisai-primary)] hover:underline">
                Uzupełnij profil firmy <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </section>

          {/* 2 karty KPI */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="biznes-kpi-strip">
            <StatCard testid="biznes-kpi-messages" Icon={MessageSquare} iconColor="#10B981" iconBg="rgba(16,185,129,0.1)"
              value={data.messages} label="Zapytania" sub={trendSub(data.messages_delta)} subColor={trendColor(data.messages_delta)} />
            <StatCard testid="biznes-kpi-visits" Icon={Eye} iconColor="#3B82F6" iconBg="rgba(59,130,246,0.1)"
              value={data.visits} label="Odwiedziny profilu" sub={trendSub(data.visits_delta)} subColor={trendColor(data.visits_delta)} />
          </section>

          {/* 2 kolumny: źródła ruchu + top oferty */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="biznes-2col">
            {/* Źródła ruchu */}
            <div className="apisai-soft-card p-5 sm:p-6" data-testid="biznes-traffic-card">
              <h3 className="text-[var(--apisai-text-primary)] text-base font-bold">Skąd trafiają klienci?</h3>
              <p className="text-[var(--apisai-text-muted)] text-[12px] mt-0.5 mb-4">Źródła odwiedzin Twojego profilu</p>
              <div className="flex items-center gap-4 mb-4">
                <DonutChart data={trafficSources} size={140} />
                <ul className="flex-1 space-y-2 text-[12px] min-w-0">
                  {trafficSources.map((s) => (
                    <li key={s.label} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                      <span className="text-[var(--apisai-text-secondary)] flex-1 truncate">{s.label}</span>
                      <span className="text-[var(--apisai-text-primary)] font-semibold flex-shrink-0">{s.pct}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Top oferty */}
            <div className="apisai-soft-card p-5 sm:p-6" data-testid="biznes-top-offers">
              <h3 className="text-[var(--apisai-text-primary)] text-base font-bold">Co przyciąga zainteresowanie?</h3>
              <p className="text-[var(--apisai-text-muted)] text-[12px] mt-0.5 mb-4">Najczęściej oglądane usługi i strona firmy</p>
              <div className="space-y-2">
                {data.top_pets.length > 0 ? data.top_pets.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-1.5">
                    <div className="w-11 h-11 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-5 h-5 text-zinc-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[var(--apisai-text-primary)] text-sm font-semibold truncate">{p.name}</p>
                      <p className="text-[12px] font-medium" style={{ color: p.color }}>{p.subtitle}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[var(--apisai-text-secondary)] text-[13px] font-semibold flex-shrink-0">
                      <Eye className="w-3.5 h-3.5 text-zinc-400" /> {p.views}
                    </span>
                  </div>
                )) : (
                  <p className="text-[var(--apisai-text-muted)] text-[13px] py-6 text-center">Dodaj usługi i zdjęcia, aby zobaczyć statystyki</p>
                )}
              </div>
              <button onClick={() => navigate(`${editPath}?krok=oferta`)} className="mt-3 inline-flex items-center gap-1 text-[var(--apisai-primary)] text-[13px] font-semibold hover:underline">Zarządzaj ofertą usług <ArrowRight className="w-3.5 h-3.5" /></button>
            </div>
          </section>

          {/* Szybkie działania */}
          <section className="space-y-4" data-testid="biznes-quick-actions">
            <div>
              <h2 className="text-[var(--apisai-text-primary)] text-xl font-bold tracking-tight">Szybkie działania</h2>
              <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-0.5">Proste kroki, które pomogą Ci zdobywać więcej klientów</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <QuickActionCard testid="biznes-action-services" Icon={Briefcase} iconColor="#8B5CF6" iconBg="rgba(139,92,246,0.1)"
                title="Edytuj ofertę usług" desc="Pełna oferta pomaga klientom wybrać właśnie Ciebie" cta="Edytuj usługi" onClick={() => navigate(`${editPath}?krok=oferta`)} />
              <QuickActionCard testid="biznes-action-photos" Icon={Camera} iconColor="#10B981" iconBg="rgba(16,185,129,0.1)"
                title="Dodaj zdjęcia do portfolio" desc="Profile ze zdjęciami realizacji mają więcej odwiedzin" cta="Dodaj zdjęcia" onClick={() => navigate(editPath)} />
              <QuickActionCard testid="biznes-action-description" Icon={FileText} iconColor="#F47A3B" iconBg="rgba(244,122,59,0.1)"
                title="Uzupełnij opis firmy" desc="Pełny opis buduje zaufanie i przyciąga klientów" cta="Edytuj opis" onClick={() => navigate(editPath)} />
            </div>
          </section>

          {/* Twoja obecność online */}
          <section
            className="rounded-2xl border px-5 sm:px-7 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6"
            style={{ background: '#FFF8F1', borderColor: 'rgba(249,115,22,0.15)' }}
            data-testid="biznes-visibility-online"
          >
            <div className="lg:col-span-2">
              <h3 className="text-[var(--apisai-text-primary)] text-lg font-bold">Twoja obecność online</h3>
              <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-0.5 mb-4">Co już działa, a co możesz dodać</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <div>
                  <PresenceRow Icon={Search} iconColor="#F97316" name="Katalog APISAI"
                    status={published ? 'Firma w katalogu' : 'Opublikuj, aby się pojawić'}
                    done={published} />
                  <PresenceRow Icon={Globe} iconColor="#10B981" name="Strona firmy"
                    status={published ? 'Strona opublikowana' : 'Opublikuj swoją stronę'}
                    done={published} />
                  <PresenceRow Icon={MessageSquare} iconColor="#3B82F6" name="Zapytania"
                    status="Formularz zapytań aktywny" done />
                </div>
                <div>
                  <PresenceRow Icon={MapPin} iconColor="#EA4335" name="Wizytówka Google"
                    status="Możesz zlecić nam konfigurację" done={false} />
                  <PresenceRow Icon={Facebook} iconColor="#1877F2" name="Facebook / Instagram"
                    status={hasSocial ? 'Linki dodane w profilu' : 'Możesz dodać linki w profilu'}
                    done={hasSocial} />
                  <button onClick={() => navigate(editPath)} className="mt-3 inline-flex items-center gap-1 text-[var(--apisai-primary)] text-[13px] font-semibold hover:underline">
                    Zarządzaj widocznością <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
            {/* Mapa lokalizacji */}
            <div className="rounded-2xl p-6 flex flex-col items-center justify-center min-h-[180px] text-center" style={{ background: '#0F1729' }} data-testid="biznes-map-card">
              <MapPin className="w-8 h-8 text-[var(--apisai-primary)] mb-2" />
              <p className="text-white text-lg font-bold">Mapa lokalizacji</p>
              <p className="text-zinc-400 text-sm mt-1">W przygotowaniu</p>
            </div>
          </section>

          {/* Przeglądaj katalog firm */}
          <section className="apisai-soft-card p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="biznes-browse-catalog-card">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(249,115,22,0.10)' }}>
              <Users className="w-6 h-6 text-[var(--apisai-primary)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-[var(--apisai-text-primary)]">Przeglądaj katalog firm</h2>
              <p className="text-sm text-[var(--apisai-text-secondary)] mt-1">Zobacz, jak wygląda katalog APISAI i gdzie pojawia się Twoja firma.</p>
            </div>
            <a href="/katalog/uslugi" target="_blank" rel="noopener noreferrer" data-testid="biznes-browse-catalog"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-xl border border-[var(--apisai-primary)] text-[var(--apisai-primary)] text-[14px] font-semibold hover:bg-[var(--apisai-primary)]/5 transition-all flex-shrink-0">
              Zobacz katalog firm <ExternalLink className="w-4 h-4" />
            </a>
          </section>
        </div>
      </StudioShell>
    );
  }

  return (
    <StudioShell mode="workspace" activeKey="catalog" contextLabel="Katalog APISAI">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 space-y-6" data-testid="panel-katalog">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h1 className="text-[var(--apisai-text-primary)] text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Katalog APISAI</h1>
            <p className="text-[var(--apisai-text-secondary)] text-sm sm:text-base">Zarządzaj swoją widocznością i sprawdzaj efekty.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {kennel?.slug && (
              <a href={`/hodowla/${kennel.slug}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--apisai-primary)] hover:underline"
                data-testid="view-public-profile">
                Zobacz mój profil w katalogu <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button onClick={() => navigate(kennel?.id ? `/wizard/hodowla-v2/${kennel.id}` : '/wizard/hodowla-v2')}
              className="inline-flex items-center gap-2 px-5 h-11 rounded-xl bg-[var(--apisai-primary)] text-white text-[14px] font-semibold hover:brightness-95 transition-all shadow-sm"
              data-testid="edit-profile">
              Edytuj profil
            </button>
          </div>
        </header>

        {/* Baner kompletności profilu */}
        <section
          className="rounded-2xl border px-5 sm:px-7 py-5 flex flex-col lg:flex-row lg:items-center gap-5"
          style={{ background: '#FFF8F1', borderColor: 'rgba(249,115,22,0.15)' }}
          data-testid="completeness-banner"
        >
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(249,115,22,0.12)' }}>
              <Sparkles className="w-6 h-6 text-[var(--apisai-primary)]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-[var(--apisai-text-primary)] text-[15px] sm:text-base font-bold">
                  {kennel?.slug ? 'Twój profil jest widoczny w katalogu APISAI' : 'Dokończ profil, aby pojawić się w katalogu'}
                </p>
                {kennel?.slug && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
              </div>
              <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-1">
                Zadbaj o kompletność profilu, aby zdobywać więcej klientów.
              </p>
            </div>
          </div>
          <div className="lg:w-72 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[13px] font-medium text-[var(--apisai-text-secondary)]">Kompletność profilu</span>
              <span className="text-[15px] font-bold text-[var(--apisai-primary)]" data-testid="completeness-value">{completeness}%</span>
            </div>
            <div className="h-2 rounded-full bg-white overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${completeness}%`, background: 'linear-gradient(90deg,#34D399,#10B981)' }} />
            </div>
            <button onClick={() => navigate(kennel?.id ? `/wizard/hodowla-v2/${kennel.id}` : '/wizard/hodowla-v2')} className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--apisai-primary)] hover:underline">
              Uzupełnij profil <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </section>

        {/* 4 karty KPI */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="kpi-strip">
          <StatCard testid="kpi-messages" Icon={MessageSquare} iconColor="#10B981" iconBg="rgba(16,185,129,0.1)"
            value={data.messages} label="Zapytania" sub={trendSub(data.messages_delta)} subColor={trendColor(data.messages_delta)} />
          {(() => {
            const posAvail = position?.available;
            const regionLabel = position?.region ? position.region.charAt(0).toUpperCase() + position.region.slice(1) : '';
            const top = posAvail && position.has_views && position.percentile <= 50;
            return (
              <StatCard testid="kpi-position" Icon={Users} iconColor="#8B5CF6" iconBg="rgba(139,92,246,0.1)"
                value={posAvail ? `#${position.position}` : '—'}
                label="Pozycja w katalogu"
                sub={posAvail
                  ? (top ? `Top ${position.percentile}% w: ${regionLabel}` : `z ${position.total} hodowli · ${regionLabel}`)
                  : 'Buduj widoczność'}
                subColor={top ? '#10B981' : '#71717A'} />
            );
          })()}
        </section>

        {/* 3 kolumny */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="katalog-3col">
          {/* Źródła ruchu */}
          <div className="apisai-soft-card p-5 sm:p-6">
            <h3 className="text-[var(--apisai-text-primary)] text-base font-bold">Skąd trafiają klienci?</h3>
            <p className="text-[var(--apisai-text-muted)] text-[12px] mt-0.5 mb-4">Źródła odwiedzin Twojego profilu</p>
            <div className="flex items-center gap-4 mb-4">
              <DonutChart data={trafficSources} size={140} />
              <ul className="flex-1 space-y-2 text-[12px] min-w-0">
                {trafficSources.map((s) => (
                  <li key={s.label} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                    <span className="text-[var(--apisai-text-secondary)] flex-1 truncate">{s.label}</span>
                    <span className="text-[var(--apisai-text-primary)] font-semibold flex-shrink-0">{s.pct}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Top pets */}
          <div className="apisai-soft-card p-5 sm:p-6">
            <h3 className="text-[var(--apisai-text-primary)] text-base font-bold">Co przyciąga zainteresowanie?</h3>
            <p className="text-[var(--apisai-text-muted)] text-[12px] mt-0.5 mb-4">Najczęściej oglądane psy i mioty</p>
            <div className="space-y-2">
              {data.top_pets.length > 0 ? data.top_pets.map((p) => (
                <div key={p.id} className="flex items-center gap-3 py-1.5">
                  {p.img ? (
                    <img src={p.img} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                      <Dog className="w-5 h-5 text-zinc-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[var(--apisai-text-primary)] text-sm font-semibold truncate">{p.name}</p>
                    <p className="text-[12px] font-medium" style={{ color: p.color }}>{p.subtitle}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[var(--apisai-text-secondary)] text-[13px] font-semibold flex-shrink-0">
                    <Eye className="w-3.5 h-3.5 text-zinc-400" /> {p.views}
                  </span>
                </div>
              )) : (
                <p className="text-[var(--apisai-text-muted)] text-[13px] py-6 text-center">Dodaj psy i mioty, aby zobaczyć statystyki</p>
              )}
            </div>
            <button onClick={() => navigate('/panel/psy')} className="mt-3 inline-flex items-center gap-1 text-[var(--apisai-primary)] text-[13px] font-semibold hover:underline">Zobacz wszystkie psy i mioty <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>
        </section>

        {/* Szybkie działania */}
        <section className="space-y-4" data-testid="growth-tips">
          <div>
            <h2 className="text-[var(--apisai-text-primary)] text-xl font-bold tracking-tight">Szybkie działania</h2>
            <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-0.5">Proste kroki, które pomogą Ci zdobywać więcej klientów</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <QuickActionCard testid="tip-photos" Icon={Camera} iconColor="#10B981" iconBg="rgba(16,185,129,0.1)"
              title="Dodaj więcej zdjęć" desc="Profile ze zdjęciami mają więcej odwiedzin" cta="Dodaj zdjęcia" onClick={() => navigate(kennel?.id ? `/wizard/hodowla-v2/${kennel.id}` : '/wizard/hodowla-v2')} />
            <QuickActionCard testid="tip-description" Icon={FileText} iconColor="#F47A3B" iconBg="rgba(244,122,59,0.1)"
              title="Uzupełnij opis hodowli" desc="Pełny opis buduje zaufanie i przyciąga klientów" cta="Edytuj opis" onClick={() => navigate(kennel?.id ? `/wizard/hodowla-v2/${kennel.id}` : '/wizard/hodowla-v2')} />
            <QuickActionCard testid="tip-whatsapp" Icon={Phone} iconColor="#10B981" iconBg="rgba(16,185,129,0.1)"
              title="Dodaj WhatsApp" desc="Klienci chętniej piszą, gdy kontakt jest łatwy" cta="Dodaj kontakt" onClick={() => navigate(kennel?.id ? `/wizard/hodowla-v2/${kennel.id}` : '/wizard/hodowla-v2')} />
          </div>
          <Link to="/panel/strona" className="inline-flex items-center gap-1 text-[var(--apisai-primary)] text-[13px] font-semibold hover:underline">
            Zobacz wszystkie wskazówki <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </section>

        {/* Twoja obecność online */}
        <section
          className="rounded-2xl border px-5 sm:px-7 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6"
          style={{ background: '#FFF8F1', borderColor: 'rgba(249,115,22,0.15)' }}
          data-testid="visibility-online"
        >
          <div className="lg:col-span-2">
            <h3 className="text-[var(--apisai-text-primary)] text-lg font-bold">Twoja obecność online</h3>
            <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-0.5 mb-4">Co już działa, a co możesz dodać</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <div>
                <PresenceRow Icon={Search} iconColor="#F97316" name="Katalog APISAI"
                  status={kennel?.slug ? 'Profil w katalogu' : 'Uzupełnij profil, aby pojawić się'}
                  done={!!kennel?.slug} />
                <PresenceRow Icon={Globe} iconColor="#10B981" name="Twoja strona"
                  status={kennel?.slug ? 'Strona opublikowana' : 'Opublikuj swoją stronę'}
                  done={!!kennel?.slug} />
                <PresenceRow Icon={MessageSquare} iconColor="#3B82F6" name="Zapytania"
                  status="Formularz zapytań aktywny" done />
              </div>
              <div>
                <PresenceRow Icon={MapPin} iconColor="#EA4335" name="Wizytówka Google"
                  status="Możesz zlecić nam konfigurację" done={false} />
                <PresenceRow Icon={Facebook} iconColor="#1877F2" name="Facebook / Instagram"
                  status="Możesz dodać linki w profilu" done={false} />
                <button onClick={() => navigate('/panel/moje')} className="mt-3 inline-flex items-center gap-1 text-[var(--apisai-primary)] text-[13px] font-semibold hover:underline">
                  Zarządzaj widocznością <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          {/* Mapa lokalizacji */}
          <div className="rounded-2xl p-6 flex flex-col items-center justify-center min-h-[180px] text-center" style={{ background: '#0F1729' }} data-testid="map-card">
            <MapPin className="w-8 h-8 text-[var(--apisai-primary)] mb-2" />
            <p className="text-white text-lg font-bold">Mapa lokalizacji</p>
            <p className="text-zinc-400 text-sm mt-1">W przygotowaniu</p>
          </div>
        </section>
      </div>
    </StudioShell>
  );
}
